import React, { useState, useEffect, useRef } from 'react';
import { useGetJobCardStateQuery } from '../api/apiSlice';

// Connection quality thresholds (in milliseconds)
const CONNECTION_THRESHOLDS = {
  STRONG: 1000,    // < 1 second
  WEAK: 5000,      // 1-5 seconds
  VERY_WEAK: 5000  // > 5 seconds
};

// Connection quality levels
export const CONNECTION_QUALITY = {
  STRONG: 'strong',
  WEAK: 'weak', 
  VERY_WEAK: 'very_weak',
  OFFLINE: 'offline'
};

// Helper function to get color based on connection quality
export const getConnectionQualityColor = (quality) => {
  switch(quality) {
    case CONNECTION_QUALITY.STRONG:
      return '#119B05'; // Green
    case CONNECTION_QUALITY.WEAK:
      return '#FFA500'; // Orange
    case CONNECTION_QUALITY.VERY_WEAK:
      return '#FF6B35'; // Red-Orange
    case CONNECTION_QUALITY.OFFLINE:
      return '#f44336'; // Red
    default:
      return '#757575'; // Grey
  }
};

// Helper function to get quality display text
export const getConnectionQualityText = (quality, responseTime = null) => {
  const timeText = responseTime ? ` (${responseTime}ms)` : '';
  switch(quality) {
    case CONNECTION_QUALITY.STRONG:
      return `Strong${timeText}`;
    case CONNECTION_QUALITY.WEAK:
      return `Weak${timeText}`;
    case CONNECTION_QUALITY.VERY_WEAK:
      return `Very Weak${timeText}`;
    case CONNECTION_QUALITY.OFFLINE:
      return 'Offline';
    default:
      return 'Unknown';
  }
};

// Hook to monitor browser online/offline status
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

// Internet check: a fetch of google.com's favicon. It decides whether the step pages show
// "Insufficient signal" and whether Home is blocked, so it has to tell slow from down: a 5 s
// timeout on every check blocked Home and every step on slow 3G while our server answered
// (stress test 26/10, P2). Now a check gets 15 s and two must fail in a row. It is one probe
// for the whole app, not one per hook instance (each step page mounted two or three), and it
// doesn't rely on AbortSignal.timeout, which Safari < 16 and Chrome < 103 lack.
const INTERNET_PROBE_URL = 'https://www.google.com/favicon.ico';
const INTERNET_TIMEOUT_MS = 15000;
const INTERNET_INTERVAL_MS = 10000;
const INTERNET_FAILURES = 2;

const internetProbe = { ok: true, failures: 0, timer: null, running: false, listeners: new Set() };

const setInternet = (ok) => {
  if (internetProbe.ok === ok) return;
  internetProbe.ok = ok;
  internetProbe.listeners.forEach((listener) => listener(ok));
};

const probeInternetOnce = async () => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), INTERNET_TIMEOUT_MS);
  try {
    await fetch(INTERNET_PROBE_URL, { mode: 'no-cors', cache: 'no-cache', signal: controller.signal });
    internetProbe.failures = 0;
    setInternet(true);
  } catch (error) {
    internetProbe.failures += 1;
    if (internetProbe.failures >= INTERNET_FAILURES) setInternet(false);
  } finally {
    clearTimeout(timeout);
  }
};

// One check at a time; the next starts INTERNET_INTERVAL_MS after the last one ends.
const runInternetProbe = async () => {
  clearTimeout(internetProbe.timer);
  if (internetProbe.running) return;
  internetProbe.running = true;
  await probeInternetOnce();
  internetProbe.running = false;
  if (internetProbe.listeners.size > 0) {
    internetProbe.timer = setTimeout(runInternetProbe, INTERNET_INTERVAL_MS);
  }
};

// Check now (Test Again, back online) rather than waiting for the next round.
export const checkInternetNow = () => { runInternetProbe(); };

export const useInternetConnection = () => {
  const [isConnected, setIsConnected] = useState(internetProbe.ok);

  useEffect(() => {
    internetProbe.listeners.add(setIsConnected);
    setIsConnected(internetProbe.ok);
    if (internetProbe.listeners.size === 1) runInternetProbe();
    window.addEventListener('online', checkInternetNow);
    return () => {
      internetProbe.listeners.delete(setIsConnected);
      window.removeEventListener('online', checkInternetNow);
      if (internetProbe.listeners.size === 0) clearTimeout(internetProbe.timer);
    };
  }, []);

  return isConnected;
};

// Hook to test API connectivity using RTK Query with response time monitoring
export const useApiConnectionViaRTK = () => {
  const [lastChecked, setLastChecked] = useState(new Date());
  const [responseTime, setResponseTime] = useState(null);
  const [avgResponseTime, setAvgResponseTime] = useState(null);
  const [connectionQuality, setConnectionQuality] = useState(CONNECTION_QUALITY.STRONG);
  const responseTimes = useRef([]);
  const startTimeRef = useRef(null);
  
  // Use existing RTK Query hook with polling for connection testing. Reconnect and focus
  // refetches need setupListeners (store.js), which was never called, so they never ran (P2).
  const {
    isError,
    isSuccess,
    isLoading,
    isFetching,
    error,
    refetch: originalRefetch
  } = useGetJobCardStateQuery(undefined, {
    pollingInterval: 15000, // Poll every 15 seconds
    refetchOnReconnect: true, // Refetch when network reconnects
    refetchOnFocus: true // Refetch when tab gets focus
  });

  const refetch = () => {
    checkInternetNow();
    return originalRefetch();
  };

  // Calculate connection quality based on response time
  const calculateConnectionQuality = (responseTime) => {
    if (responseTime === null) return CONNECTION_QUALITY.OFFLINE;
    if (responseTime < CONNECTION_THRESHOLDS.STRONG) return CONNECTION_QUALITY.STRONG;
    if (responseTime < CONNECTION_THRESHOLDS.WEAK) return CONNECTION_QUALITY.WEAK;
    return CONNECTION_QUALITY.VERY_WEAK;
  };

  // Calculate rolling average of last 5 response times
  const updateResponseTimes = (newTime) => {
    responseTimes.current = [...responseTimes.current, newTime].slice(-5); // Keep only last 5
    const average = responseTimes.current.reduce((sum, time) => sum + time, 0) / responseTimes.current.length;
    setAvgResponseTime(Math.round(average));
  };

  // Time every check, the 15 s polls included. Only a manual refetch was timed, so the
  // dialog's "Last checked" stayed at page load and its rows lagged the headline (P2).
  useEffect(() => {
    if (isFetching) {
      startTimeRef.current = performance.now();
      return;
    }
    if ((isSuccess || isError) && startTimeRef.current !== null) {
      const endTime = performance.now();
      const currentResponseTime = Math.round(endTime - startTimeRef.current);
      
      setResponseTime(currentResponseTime);
      setLastChecked(new Date());
      startTimeRef.current = null;
      
      // Only update response times if API actually responded (not network error)
      if (isSuccess || (isError && error?.status)) {
        updateResponseTimes(currentResponseTime);
        setConnectionQuality(calculateConnectionQuality(currentResponseTime));
      } else {
        // Network error - treat as offline
        setConnectionQuality(CONNECTION_QUALITY.OFFLINE);
      }
    }
  }, [isFetching, isSuccess, isError, error]);

  // The server is reachable if it answered at all below 500: a 401 or 403 is the server
  // speaking, not the network failing. Only a 401 counted, so a 403 read as "offline" (N19).
  const isApiConnected = isSuccess || (isError && typeof error?.status === "number" && error.status < 500);
  // Whether the check has an answer yet. Home treated a check still in flight as a failure
  // and showed "No network connection" on every load until it returned (N19).
  const apiChecked = isSuccess || isError;
  
  // Determine if connection is weak
  const isWeakConnection = connectionQuality === CONNECTION_QUALITY.WEAK || 
                           connectionQuality === CONNECTION_QUALITY.VERY_WEAK;

  return {
    isApiConnected,
    apiChecked,
    isWeakConnection,
    connectionQuality,
    responseTime,
    avgResponseTime,
    lastChecked,
    isLoading,
    refetch,
    error: isError ? error : null
  };
};

// Comprehensive connection test hook combining all connection types
export const useComprehensiveConnectionTest = () => {
  const networkStatus = useNetworkStatus();
  const internetConnection = useInternetConnection();
  const { 
    isApiConnected, 
    apiChecked,
    isWeakConnection,
    connectionQuality,
    responseTime,
    avgResponseTime,
    lastChecked, 
    isLoading, 
    refetch, 
    error 
  } = useApiConnectionViaRTK();

  // Offline, the last internet and server checks are out of date: say so at once rather than
  // show "Connected" / "Reachable" under a "No network connection" headline (P2).
  const internetOk = networkStatus && internetConnection;
  const serverOk = networkStatus && isApiConnected;
  const isFullyConnected = internetOk && serverOk;
  // A real failure: offline, no internet, or a finished server check that failed.
  const connectionFailed = !networkStatus || !internetConnection || (apiChecked && !isApiConnected);

  // Helper function to get API status text with timing info
  const getApiStatusText = () => {
    if (!isApiConnected) return 'API Unavailable';
    if (responseTime === null) return 'API Responding';
    
    const timeText = avgResponseTime ? ` (avg: ${avgResponseTime}ms)` : ` (${responseTime}ms)`;
    switch(connectionQuality) {
      case CONNECTION_QUALITY.STRONG:
        return `API Fast${timeText}`;
      case CONNECTION_QUALITY.WEAK:
        return `API Slow${timeText}`;
      case CONNECTION_QUALITY.VERY_WEAK:
        return `API Very Slow${timeText}`;
      default:
        return `API Responding${timeText}`;
    }
  };

  return {
    isFullyConnected,
    networkStatus,
    internetConnection: internetOk,
    isApiConnected: serverOk,
    isWeakConnection,
    connectionQuality,
    responseTime,
    avgResponseTime,
    lastChecked,
    isLoading,
    refetch,
    error,
    connectionFailed,
    // The flags are what ConnectionBlockingDialog reads; it only got the strings, so it
    // always titled itself "No network connection" and marked every row failed (N19).
    connectionDetails: {
      networkStatus,
      internetConnection: internetOk,
      isApiConnected: serverOk,
      network: networkStatus ? 'Online' : 'Offline',
      internet: internetOk ? 'Connected' : 'No Internet',
      api: getApiStatusText()
    }
  };
};

// Enhanced hook for individual installer pages with connection quality
export const useConnectionMonitor = () => {
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [weakWarningDismissed, setWeakWarningDismissed] = useState(false);
  
  // Get comprehensive connection info including response times
  const { 
    isFullyConnected,
    networkStatus,
    internetConnection,
    isApiConnected,
    isWeakConnection,
    connectionQuality,
    responseTime,
    avgResponseTime,
    connectionDetails: fullConnectionDetails,
    refetch
  } = useComprehensiveConnectionTest();

  // Only consider network and internet for basic connectivity
  const isConnected = networkStatus && internetConnection;
  
  // Show alert when connection is lost (and not manually dismissed)
  const showAlert = !isConnected && !alertDismissed;
  
  // Show weak connection warning when API is slow (and not dismissed)
  const showWeakWarning = isFullyConnected && isWeakConnection && !weakWarningDismissed;

  // Reset dismissals when connection changes
  useEffect(() => {
    if (isConnected) {
      setAlertDismissed(false); // Reset when connection restored
    }
    if (!isWeakConnection) {
      setWeakWarningDismissed(false); // Reset when connection improves
    }
  }, [isConnected, isWeakConnection]);

  const handleRetry = () => {
    // Clear dismissals and refetch API data
    setAlertDismissed(false);
    setWeakWarningDismissed(false);
    if (refetch) refetch();
  };

  const handleDismiss = () => {
    setAlertDismissed(true);
  };

  const handleWeakWarningDismiss = () => {
    setWeakWarningDismissed(true);
  };

  const connectionDetails = {
    networkStatus,
    internetConnection,
    isApiConnected,
    network: networkStatus ? 'Online' : 'Offline',
    internet: internetConnection ? 'Connected' : 'No Internet',
    api: fullConnectionDetails.api
  };

  return {
    isConnected,
    isFullyConnected,
    isWeakConnection,
    connectionQuality,
    responseTime,
    avgResponseTime,
    showAlert,
    showWeakWarning,
    handleRetry,
    handleDismiss,
    handleWeakWarningDismiss,
    connectionDetails
  };
};